import React from 'react';
import { Button, ButtonProps, Box } from '@mui/material';
import { Star as StarIcon, Upgrade as UpgradeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { isPremiumUser } from '@utils/premium';
import LogoHPPlus from './LogoHPPlus';

interface UpgradeButtonProps extends Omit<ButtonProps, 'onClick'> {
  /**
   * Varianta button-ului: 'contained' pentru gradient, 'outlined' pentru outline
   */
  variant?: 'contained' | 'outlined';
  
  /**
   * Size: 'small', 'medium', 'large'
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * URL pentru redirect (default: '/plans')
   */
  redirectTo?: string;
  
  /**
   * Show icon sau doar text
   */
  showIcon?: boolean;
  
  /**
   * Text custom pentru button
   */
  customText?: string;
  
  /**
   * Auto-hide dacă utilizatorul este deja premium
   */
  hideIfPremium?: boolean;
}

/**
 * Componentă UpgradeButton - Buton pentru upgrade către Healthypaws+ Premium
 * 
 * Folosire:
 * ```tsx
 * <UpgradeButton variant="contained" size="medium" />
 * <UpgradeButton variant="outlined" hideIfPremium customText="Upgrade acum" />
 * ```
 */
const UpgradeButton: React.FC<UpgradeButtonProps> = ({
  variant = 'contained',
  size = 'medium',
  redirectTo = '/plans',
  showIcon = true,
  customText,
  hideIfPremium = false,
  ...buttonProps
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Auto-hide dacă utilizatorul este deja premium
  if (hideIfPremium && isPremiumUser(user)) {
    return null;
  }
  
  const buttonText = customText || 'Activează Healthypaws+';
  
  const gradientStyles = variant === 'contained' ? {
    background: 'linear-gradient(135deg, #5A80C8 0%, #4A70B8 50%, #3A60A8 100%)',
    color: 'white',
    fontWeight: 700,
    '&:hover': {
      background: 'linear-gradient(135deg, #4A70B8 0%, #3A60A8 50%, #2A5088 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(90,128,200,0.3)'
    },
    transition: 'all 0.2s ease-in-out'
  } : {
    borderColor: '#5A80C8',
    color: '#5A80C8',
    fontWeight: 600,
    '&:hover': {
      borderColor: '#4A70B8',
      bgcolor: 'rgba(90, 128, 200, 0.08)',
      transform: 'translateY(-1px)'
    },
    transition: 'all 0.2s ease-in-out'
  };
  
  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => navigate(redirectTo)}
      startIcon={showIcon ? <LogoHPPlus variant="pawPlus" size={18} /> : undefined}
      sx={{
        ...gradientStyles,
        ...buttonProps.sx,
        '& .MuiButton-startIcon': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
      {...buttonProps}
    >
      {buttonText}
    </Button>
  );
};

export default UpgradeButton;

